import { SearchParameters } from 'algoliasearch-helper'
import { Axios, ReactQuery } from 'cms-api'
import {
  type GetStaticPaths,
  type GetStaticProps,
  type NextPageWithLayout,
} from 'next'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import React from 'react'
import { InstantSearch } from 'react-instantsearch'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { getPublicLayout } from '@public/components/PublicLayout'
import {
  EXCLUDE_CUSTOM_SALES_FILTER,
  ShopPageLayout,
} from 'components/InstantSearch'
import { BRAND_PAGE_SORT_OPTIONS } from 'components/InstantSearch/TypeSenseSortBy'
import { Seo } from 'components/Seo'
import {
  EXPERT_SHOWROOMS_PAGE_SLUG,
  SHOWROOM_BRANDS_PAGE_SLUG,
} from 'constants/common'
import { ROUTE } from 'constants/routes'
import { CmsShowroomNavigationPageSlugsDocument } from 'types/graphql-generated'
import { initializeApollo } from 'utils/apolloClient'
import { getTypesenseConfigurationOptions } from 'utils/typesenseClient'
import { categoryRedirects } from './categoryPagesRedirects'
import { expertShowroomsRedirects } from './expertShowroomsRedirects'
import { useCmsShowroomPageBySlug } from './hooks'
import { prepareGetStaticPropsForShowroomPage } from './prepareGetStaticPropsForShowroomPage'
import { ShowroomPageView } from './ShowroomPageView'

const { searchClient } = new TypesenseInstantSearchAdapter({
  server: getTypesenseConfigurationOptions(),
  additionalSearchParameters: {
    query_by: 'title,embedding',
    include_fields:
      'id,title,imageUrl,imageAlt,minPrice,maxPrice,minTradePrice,maxTradePrice,minTradeDiscount,legacyProductId,breakoutOptionCount,carouselVariants,legacyProductVariantId,brand,availabilityOrder, itemTypes',
    sort_by: BRAND_PAGE_SORT_OPTIONS[1].value.replace('products-v5/sort/', ''),
  },
})

interface ShowroomPageProps {
  slug: string
  isPreview?: boolean
}

export const ShowroomPage: NextPageWithLayout<ShowroomPageProps> = ({
  slug,
  isPreview,
}) => {
  const { data: shopNavigation, isLoading: isLoadingShopNavigation } =
    ReactQuery.useGetNavigationRenderIdOrSlug('shop-navigation')

  const strapiShopNavigationDocumentId = shopNavigation.find(
    ({ path }) => path === `/shop/${slug}`
  )?.related?.documentId

  const { data: strapiShopPages, isLoading: isLoadingStrapiShopPage } =
    ReactQuery.useGetShopPages(
      {
        filters: {
          documentId: strapiShopNavigationDocumentId,
        },
        populate: {
          seoHeader: '*',
          productCollections: {
            fields: 'id',
          },
        } as never,
      },
      {
        query: {
          enabled: !!strapiShopNavigationDocumentId,
        },
      }
    )

  const strapiShopPage = strapiShopPages?.data?.[0]

  const {
    pageBySlug,
    isLoading: isLoadingCmsShowroomPage,
    widgets,
  } = useCmsShowroomPageBySlug({
    slug,
    isPreview,
    forceSkip:
      isLoadingShopNavigation || isLoadingStrapiShopPage || !!strapiShopPage,
  })

  const filters = [
    'status:=PUBLISHED',
    EXCLUDE_CUSTOM_SALES_FILTER,
    `productCollectionIds:[${(strapiShopPage?.productCollections ?? []).map(({ id }) => id).join(',')}]`,
  ].join(' && ')

  const searchParameters = useMemo(
    () =>
      new SearchParameters({
        hitsPerPage: 32,
        filters,
      }),
    [filters]
  )
  const [routingKey, setRoutingKey] = useState(0)
  const router = useRouter()

  useEffect(() => {
    setRoutingKey((k) => k + 1)
  }, [router.asPath])

  const isLoading =
    isLoadingCmsShowroomPage ||
    isLoadingShopNavigation ||
    isLoadingStrapiShopPage

  const isShowroomExpertsPage = slug === EXPERT_SHOWROOMS_PAGE_SLUG

  if (strapiShopPage) {
    return (
      <>
        <Seo
          title={strapiShopPage.title}
          description={
            strapiShopPage.seoHeader?.description ??
            `Shop ${strapiShopPage.title}`
          }
          keywords={strapiShopPage.seoHeader?.keywords?.split(', ')}
          metaTitle={strapiShopPage.seoHeader?.metaTitle}
        />
        <InstantSearch
          key={routingKey}
          searchClient={searchClient}
          indexName="products-v5"
          routing
        >
          <ShopPageLayout
            searchParameters={searchParameters}
            showParentLevel={false}
            defaultSortIndex={1}
          />
        </InstantSearch>
      </>
    )
  }

  return (
    <ShowroomPageView
      isPreview={isPreview}
      slug={slug}
      data={pageBySlug}
      isLoading={isLoading}
      widgets={widgets}
      canonicalUrl={
        slug === SHOWROOM_BRANDS_PAGE_SLUG
          ? undefined
          : ROUTE.SHOWROOM_DETAIL({ slug })
      }
      isSearchFilterParamsMode
      {...(pageBySlug && {
        fallbackMetaTitle: isShowroomExpertsPage
          ? 'The Expert - Shop Designer Curated Home Decor'
          : `${pageBySlug.cmsPage.title}`,
        fallbackDescription: isShowroomExpertsPage
          ? 'Get the look of your favorite interior designer by shopping the Showrooms of Experts from Jake Arnold to Jenna Lyons. Discover vintage and new furniture, lighting, rugs, and more.'
          : `Shop ${pageBySlug.cmsPage.title}`,
      })}
    />
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const shopNavigation =
    await Axios.client().getNavigationRenderIdOrSlug('shop-navigation')

  const strapiShopPages = shopNavigation.filter(
    ({ type }) => type !== 'WRAPPER'
  )

  const strapiShopSlugs = strapiShopPages.map(({ path }) =>
    path.split('/').at(-1)
  )

  const apolloClient = initializeApollo()
  const { data: { cmsShowroomNavigationPageSlugs = [] } = {} } =
    await apolloClient.query({
      query: CmsShowroomNavigationPageSlugsDocument,
    })
  const skippedPaths = [...categoryRedirects, ...expertShowroomsRedirects].map(
    ({ source }) => source
  )

  const paths = [...cmsShowroomNavigationPageSlugs, ...strapiShopSlugs].reduce(
    (filteredPaths, slug: string) => {
      if (skippedPaths.includes(slug)) {
        return filteredPaths
      }
      return [
        ...filteredPaths,
        {
          params: { slug },
        },
      ]
    },
    []
  )

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params.slug as string
  const routeToRedirect = [
    ...categoryRedirects,
    ...expertShowroomsRedirects,
  ].find(({ source }) => source === slug)

  if (!routeToRedirect) {
    const baseProps = await prepareGetStaticPropsForShowroomPage(context, {
      slug,
      route: ROUTE.SHOWROOM_DETAIL({ slug }),
    })

    const shopNavigation =
      await Axios.client().getNavigationRenderIdOrSlug('shop-navigation')

    const strapiShopNavigation = shopNavigation.find(
      ({ path }) => path === `/shop/${slug}`
    )

    return {
      ...baseProps,
      revalidate: 24 * 3600,
      props: {
        ...baseProps.props,
        ...(strapiShopNavigation
          ? {
              breadcrumbs: [
                ...baseProps.props.breadcrumbs,
                {
                  label: strapiShopNavigation.title,
                  link: strapiShopNavigation.path,
                },
              ],
            }
          : undefined),
      },
    }
  }
  return {
    revalidate: 24 * 3600,
    redirect: {
      destination: ROUTE.SHOWROOM_DETAIL({
        slug: routeToRedirect.destination,
      }),
      permanent: false,
    },
  }
}

const PageLayout = (page: ReactNode) =>
  getPublicLayout(page, { isFullWidth: true, isWithBreadcrumbs: true })

ShowroomPage.getLayout = PageLayout
